import { GroupEntity } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from '@material-ui/lab/Alert';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAsync, useAsyncRetry } from 'react-use';
import { JiraProjectOption } from '../jiraBoards/jiraBoardTypes';
import {
  OrganizationGroupNode,
  buildOrganizationGroupTree,
  collectOrganizationGroupNodeNames,
  countOrganizationGroupNodes,
  enrichOrganizationGroupRows,
  filterOrganizationGroupTree,
  isInvisibleOrgGroup,
  splitRootOrganization,
} from './buildOrganizationGroupRows';
import { UpdateTeamBoardMappingDialog } from './UpdateTeamBoardMappingDialog';

const INDENT_PX = 28;
const TOGGLE_CELL_WIDTH = 40;

const useStyles = makeStyles(theme => ({
  rootHeader: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  treeRoot: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'stretch',
    minHeight: 52,
    borderTop: `1px solid ${theme.palette.divider}`,
    '&:first-of-type': {
      borderTop: 'none',
    },
  },
  toggleCell: {
    width: TOGGLE_CELL_WIDTH,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleSpacer: {
    width: TOGGLE_CELL_WIDTH,
    flexShrink: 0,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2, 1.25, 0),
  },
  rowTitle: {
    minWidth: 0,
  },
  rowLink: {
    color: 'inherit',
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  typeChip: {
    textTransform: 'capitalize',
  },
  boardChip: {
    fontFamily: theme.typography.fontFamily,
  },
  childrenBlock: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

function OrganizationGroupTreeItem({
  node,
  depth,
  expandedNames,
  onToggle,
  canUpdateBoard,
  onEditBoard,
}: {
  node: OrganizationGroupNode;
  depth: number;
  expandedNames: Set<string>;
  onToggle: (name: string) => void;
  canUpdateBoard: boolean;
  onEditBoard: (node: OrganizationGroupNode) => void;
}) {
  const classes = useStyles();
  const hasChildren = node.children.length > 0;
  const expanded = expandedNames.has(node.name);

  return (
    <>
      <Box className={classes.row} style={{ paddingLeft: depth * INDENT_PX }}>
        <Box className={classes.toggleCell}>
          {hasChildren ? (
            <IconButton
              size="small"
              aria-label={expanded ? `Collapse ${node.displayName}` : `Expand ${node.displayName}`}
              onClick={() => onToggle(node.name)}
            >
              {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box className={classes.toggleSpacer} />
          )}
        </Box>
        <Box className={classes.rowMain}>
          <Box className={classes.rowTitle}>
            <Link component={RouterLink} to={node.catalogPath} className={classes.rowLink}>
              {node.displayName}
            </Link>
            <Typography variant="caption" color="textSecondary" display="block">
              {node.name}
            </Typography>
          </Box>
          <Box className={classes.metaRow}>
            <Chip size="small" label={node.type} variant="outlined" className={classes.typeChip} />
            {node.projectKeys.length > 0 ? (
              node.projectKeys.map(key => (
                <Chip key={key} size="small" label={key} variant="outlined" className={classes.boardChip} />
              ))
            ) : (
              <Typography variant="caption" color="textSecondary">
                No Jira boards
              </Typography>
            )}
            {node.manager ? (
              <Typography variant="caption" color="textSecondary">
                Manager: {node.manager}
              </Typography>
            ) : null}
            {hasChildren ? (
              <Typography variant="caption" color="textSecondary">
                {node.children.length} direct
              </Typography>
            ) : null}
            {canUpdateBoard ? (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => onEditBoard(node)}
              >
                Boards
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>

      {hasChildren ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box className={classes.childrenBlock}>
            {node.children.map(child => (
              <OrganizationGroupTreeItem
                key={child.entityRef}
                node={child}
                depth={depth + 1}
                expandedNames={expandedNames}
                onToggle={onToggle}
                canUpdateBoard={canUpdateBoard}
                onEditBoard={onEditBoard}
              />
            ))}
          </Box>
        </Collapse>
      ) : null}
    </>
  );
}

function OrganizationGroupTree({
  nodes,
  expandedNames,
  onToggle,
  canUpdateBoard,
  onEditBoard,
}: {
  nodes: OrganizationGroupNode[];
  expandedNames: Set<string>;
  onToggle: (name: string) => void;
  canUpdateBoard: boolean;
  onEditBoard: (node: OrganizationGroupNode) => void;
}) {
  const classes = useStyles();

  if (nodes.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No groups match your search.
      </Typography>
    );
  }

  return (
    <Box className={classes.treeRoot}>
      {nodes.map(node => (
        <OrganizationGroupTreeItem
          key={node.entityRef}
          node={node}
          depth={0}
          expandedNames={expandedNames}
          onToggle={onToggle}
          canUpdateBoard={canUpdateBoard}
          onEditBoard={onEditBoard}
        />
      ))}
    </Box>
  );
}

function defaultExpandedNames(nodes: OrganizationGroupNode[]): Set<string> {
  return new Set(nodes.filter(node => node.children.length > 0).map(node => node.name));
}

export function OrganizationGroupsPanel() {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  // Any authenticated user can open a board-update PR (the backend gates on the
  // jiraBoardUpdate permission + human PR review), so the button is always shown.
  const canUpdateBoard = true;
  const [search, setSearch] = useState('');
  const [expandedNames, setExpandedNames] = useState<Set<string>>(() => new Set());
  const [dialogNode, setDialogNode] = useState<OrganizationGroupNode | undefined>();

  const groupsState = useAsyncRetry(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'Group' },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.annotations',
        'spec',
      ],
    });
    const groups = (
      response.items.filter(entity => entity.kind === 'Group') as GroupEntity[]
    ).filter(isInvisibleOrgGroup);
    return buildOrganizationGroupTree(enrichOrganizationGroupRows(groups));
  }, [catalogApi]);

  const jiraProjectsState = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('jira');
    const response = await fetchApi.fetch(`${baseUrl}/projects`, { credentials: 'include' });
    if (!response.ok) {
      return [] as JiraProjectOption[];
    }
    const payload = (await response.json()) as { projects?: JiraProjectOption[] };
    return payload.projects ?? [];
  }, [discoveryApi, fetchApi]);

  const filteredTree = useMemo(
    () => filterOrganizationGroupTree(groupsState.value ?? [], search),
    [groupsState.value, search],
  );

  const { root, tree } = useMemo(() => splitRootOrganization(filteredTree), [filteredTree]);

  const searchActive = Boolean(search.trim());
  const totalGroups = countOrganizationGroupNodes(groupsState.value ?? []);
  const visibleGroups = countOrganizationGroupNodes(filteredTree);

  useEffect(() => {
    if (searchActive) {
      setExpandedNames(collectOrganizationGroupNodeNames(tree));
      return;
    }

    setExpandedNames(defaultExpandedNames(tree));
  }, [searchActive, tree]);

  const handleToggle = (name: string) => {
    setExpandedNames(current => {
      const next = new Set(current);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  if (groupsState.loading) {
    return <Progress />;
  }

  if (groupsState.error) {
    return <Alert severity="error">{groupsState.error.message}</Alert>;
  }

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gridGap={12}
        style={{ marginBottom: 16 }}
      >
        <Typography variant="body2" color="textSecondary">
          {visibleGroups} of {totalGroups} group(s) from the invisible org catalog
        </Typography>
        <Box width="100%" maxWidth={420} minWidth={240}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Search"
            placeholder="Name, type, manager, or board"
            value={search}
            onChange={event => setSearch(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      {root ? (
        <Box className={classes.rootHeader}>
          <Link component={RouterLink} to={root.catalogPath} className={classes.rowLink}>
            <Typography variant="h6" component="span">
              {root.displayName}
            </Typography>
          </Link>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Root organization · {root.children.length} top-level group(s)
          </Typography>
        </Box>
      ) : null}

      <OrganizationGroupTree
        nodes={tree}
        expandedNames={expandedNames}
        onToggle={handleToggle}
        canUpdateBoard={canUpdateBoard}
        onEditBoard={setDialogNode}
      />

      {dialogNode ? (
        <UpdateTeamBoardMappingDialog
          open
          onClose={() => setDialogNode(undefined)}
          entityRef={dialogNode.entityRef}
          groupDisplayName={dialogNode.displayName}
          currentProjectKeys={dialogNode.declaredProjectKeys}
          inheritedProjectKeys={dialogNode.inheritsBoards ? dialogNode.projectKeys : []}
          jiraProjects={jiraProjectsState.value ?? []}
          onUpdated={() => groupsState.retry()}
        />
      ) : null}
    </>
  );
}
